/*
 *  TTTech nerve-management-system-frontend
 *  Copyright(c) 2026. TTTech Industrial Automation AG.
 *
 *  ALL RIGHTS RESERVED.
 *
 *  Usage of this software, including source code, netlists, documentation,
 *  is subject to restrictions and conditions of the applicable license
 *  agreement with TTTech Industrial Automation AG or its affiliates.
 *
 *  All trademarks used are the property of their respective owners.
 *
 *  TTTech Industrial Automation AG and its affiliates do not assume any liability
 *  arising out of the application or use of any product described or shown
 *  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
 *  make changes, at any time, in order to improve reliability, function or
 *  design.
 *
 *  Contact Information:
 *  support@tttech-industrial.com
 *
 *  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
 *
 */

import { defineStore } from 'pinia';
import SnapshotModel from '@/model/snapshot/snapshot.model';
import SnapshotScheduleModel from '@/model/snapshot/snapshot-schedule.model';
import { SnapshotApiService } from '@/services/api';
import useUtilsStore from '@/store/modules/pinia/utils/_api_request_handler';
import logger from '@/plugins/logger';

const useVmSnapshotStore = defineStore('vmSnapshot', {
  state: () => ({
    vmSnapshotList: [],
    scheduleObj: {},
    deviceId: '',
    serialNumber: '',
    isScheduleSaveButtonDisabled: true,
    isScheduleDeleteButtonDisabled: true,
  }),

  actions: {
    async fetch(deviceId, serialNumber) {
      try {
        // If either deviceId or serialNumber does not exist axios request should not be sent
        if (!deviceId || !serialNumber) {
          return;
        }
        const { snapshots, scheduled } = await SnapshotApiService.fetchSnapshots({ deviceId, serialNumber });
        this.vmSnapshotList = snapshots.map((snapshot) => new SnapshotModel(snapshot));
        this.scheduleObj = new SnapshotScheduleModel(scheduled);
        if (scheduled && scheduled.type && scheduled.type !== '') {
          this.$patch({
            isScheduleDeleteButtonDisabled: false,
            isScheduleSaveButtonDisabled: true,
          });

          return;
        }
        this.$patch({
          isScheduleSaveButtonDisabled: true,
          isScheduleDeleteButtonDisabled: true,
        });
      } catch (e) {
        this.$patch({
          vmSnapshotList: [],
          scheduleObj: {},
          isScheduleSaveButtonDisabled: true,
          isScheduleDeleteButtonDisabled: true,
        });

        throw e;
      }
    },
    async create(params) {
      try {
        await SnapshotApiService.create(params);
      } catch (e) {
        logger.debug(e);
        throw e;
      }
    },
    async remove(snapshot) {
      await SnapshotApiService.remove({
        deviceId: this.deviceId,
        serialNumber: this.serialNumber,
        snapshotName: snapshot?.name,
        workloadId: snapshot?.workloadId?.split('-')[0],
        versionId: snapshot?.versionId?.split('-')[0],
      });
      this.vmSnapshotList.splice(
        this.vmSnapshotList.findIndex((s) => s.name === snapshot.name),
        1,
      );
      const utilsStore = useUtilsStore();
      utilsStore.showCustomToast({
        text: 'vmSnapshot.removeSnapshot',
        color: 'success',
        showClose: true,
      });
    },

    async restore(params) {
      try {
        await SnapshotApiService.restore(params);
      } catch (e) {
        logger.debug(e);
        throw e;
      }
    },

    async schedule(params) {
      try {
        await SnapshotApiService.schedule(params);
        this.isScheduleSaveButtonDisabled = true;
        this.isScheduleDeleteButtonDisabled = false;
      } catch (e) {
        logger.debug(e);
        throw e;
      }
    },

    async removeSchedule(params) {
      try {
        await SnapshotApiService.removeSchedule(params);
        this.scheduleObj = null;
        this.isScheduleSaveButtonDisabled = true;
        this.isScheduleDeleteButtonDisabled = true;
      } catch (e) {
        logger.debug(e);
        throw e;
      }
    },

    setDeviceId(deviceId) {
      this.deviceId = deviceId;
    },

    setSerialNumber(serialNumber) {
      this.serialNumber = serialNumber;
    },

    showToast({ successMsg, errorMsg, error }) {
      const knownErrors = ['SNAPSHOT_CREATION_FAILED_NO_SPACE_LEFT_ON_DEVICE'];

      const text = !error ? successMsg : knownErrors.indexOf(error) !== -1 ? `errorMessages.${error}` : errorMsg;
      const utilsStore = useUtilsStore();
      utilsStore.showCustomToast({
        text,
        color: !error ? 'success' : 'red',
        showClose: true,
      });
    },

    mqttSnapshot(payload) {
      const { sessionId } = JSON.parse(localStorage.getItem('session'));

      const {
        params: { data },
      } = payload;

      // Only the message with the appropriate deviceId is considered
      if (!data || !data.deviceId || parseInt(data.deviceId, 10) !== this.deviceId) {
        return;
      }
      const actionName = payload.params.name;

      // If you are the user who initiated the create/restore snapshot request
      // close loading bar and show the success/error toast
      if (data.sessionId === sessionId) {
        const utilsStore = useUtilsStore();
        utilsStore.closeLoadingBar();
        if (actionName === 'snapshot_created') {
          this.showToast({
            successMsg: 'vmSnapshot.saveSnapshotMessage',
            errorMsg: 'errorMessages.nerve_workload_047',
            error: data.error,
          });
        } else if (actionName === 'snapshot_reverted') {
          this.showToast({
            successMsg: 'vmSnapshot.restoreSnapshotMessage',
            errorMsg: 'errorMessages.nerve_workload_049',
            error: data.error,
          });
        }
      }

      if (actionName === 'snapshot_created' && !data.error) {
        const { snapshot } = payload.params.data;
        const already = this.vmSnapshotList.find((s) => s.name === snapshot.name);
        if (!already) {
          this.vmSnapshotList.push(new SnapshotModel(snapshot));
        }
      }
      if (actionName === 'snapshot_deleted') {
        this.vmSnapshotList.splice(
          this.vmSnapshotList.findIndex((snapshot) => snapshot.name === payload.params.data.name),
          1,
        );
      }
      if (actionName === 'schedule_snapshot_created') {
        this.vmSnapshotList = payload.params.data.snapshots.map((snapshot) => new SnapshotModel(snapshot));
      }
      if (actionName === 'schedule_config_created') {
        this.scheduleObj = new SnapshotScheduleModel(payload.params.data.scheduled);
        this.isScheduleSaveButtonDisabled = true;
        this.isScheduleDeleteButtonDisabled = false;
      }
      if (actionName === 'schedule_config_deleted') {
        this.scheduleObj = null;
        this.isScheduleSaveButtonDisabled = true;
        this.isScheduleDeleteButtonDisabled = true;
      }
    },
  },
});

export default useVmSnapshotStore;
